import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { theme, neutral, jadeGreen, garnetRed, lightBlue, goldenTopaz } from '../design-system/theme';
import { AdminShell } from '../components/admin/AdminShell';
import { adminApi } from '../services/adminApi';
import { formatDate, formatTime } from '../utils/dateFormat';
import {
  PageHeader as SharedPageHeader,
  FilterGroup as SharedFilterGroup,
  SearchInput as SharedSearchInput,
  SelectInput,
  Table,
  Th,
  Td,
  Tr,
  StatusBadge,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalBody,
  CloseButton,
  AlertMessage,
  EmptyStateContainer,
  EmptyStateIcon,
  EmptyStateTitle,
  EmptyStateText,
  LoadingSpinner,
  PaginationContainer,
  PaginationInfo,
  PaginationButtons,
  PaginationButton,
  ActionBadge as SharedActionBadge,
} from '../styles/adminShared';

// Types
interface AuditLog {
  auditId: string;
  timestamp: string;
  action: string;
  entityType: string;
  entityId: string;
  actorId?: string;
  actorType?: string;
  actorEmail?: string;
  actorRole?: string;
  ipAddress?: string;
  userAgent?: string;
  requestId?: string;
  changeDescription?: string;
  result: string;
  errorMessage?: string;
  httpStatusCode?: number;
  gdprRelevant?: boolean;
  dpdpRelevant?: boolean;
  complianceNotes?: string;
  metadata?: Record<string, string>;
  projectId?: string;
  consentStatus?: string;
}

// Styled Components
const TitleSection = styled.div`
  h1 {
    color: ${theme.textPrimary};
    margin: 0;
    font-size: 24px;
  }
  
  p {
    color: ${theme.textSecondary};
    margin: 8px 0 0 0;
    font-size: 14px;
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 24px;
  padding: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const FilterGroupWithLabel = styled(SharedFilterGroup)`
  flex-direction: column;
  gap: 4px;
  
  label {
    font-size: 12px;
    color: ${theme.textSecondary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const ClearButton = styled.button`
  padding: 10px 16px;
  border: 2px solid ${neutral[4]};
  border-radius: 8px;
  background: white;
  color: ${theme.textSecondary};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  align-self: flex-end;
  
  &:hover {
    background: ${neutral[1]};
    border-color: ${theme.primary};
    color: ${theme.primary};
  }
`;

const ResultBadge = styled(StatusBadge)`
`;

const EntityTag = styled.span`
  display: inline-block;
  padding: 6px 12px;
  background: ${lightBlue[1]};
  color: ${lightBlue[7]};
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
`;

const ComplianceBadges = styled.div`
  display: flex;
  gap: 4px;
`;

const ComplianceBadge = styled.span<{ $type: 'gdpr' | 'dpdp' }>`
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: ${props => props.$type === 'gdpr' ? lightBlue[1] : goldenTopaz[1]};
  color: ${props => props.$type === 'gdpr' ? lightBlue[7] : goldenTopaz[8]};
`;

const TimeCell = styled.div`
  .date {
    font-weight: 500;
    color: ${theme.textPrimary};
  }
  .time {
    font-size: 12px;
    color: ${theme.textSecondary};
    margin-top: 4px;
  }
`;

const ActorCell = styled.div`
  .email {
    font-weight: 500;
    color: ${theme.textPrimary};
  }
  .role {
    font-size: 12px;
    color: ${theme.textSecondary};
    margin-top: 2px;
  }
  .ip {
    font-size: 11px;
    color: ${theme.textSubtle};
    font-family: monospace;
    margin-top: 2px;
  }
`;

const ExportButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
  box-shadow: 0 4px 14px rgba(99, 102, 241, 0.25);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.35);
  }
`;

const ConsentStatusBadge = styled(StatusBadge)``;

const VerifyButton = styled.button`
  padding: 10px 20px;
  border-radius: 8px;
  border: 2px solid ${neutral[4]};
  background: white;
  color: ${theme.textPrimary};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    background: ${neutral[1]};
    border-color: ${theme.primary};
    color: ${theme.primary};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const VerifyResult = styled.div<{ $verified: boolean }>`
  padding: 12px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${props => props.$verified ? jadeGreen[1] : garnetRed[1]};
  color: ${props => props.$verified ? jadeGreen[7] : garnetRed[6]};
`;

const ErrorState = styled(AlertMessage)``;

const DetailSection = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h4`
  margin: 0 0 12px 0;
  color: ${theme.textPrimary};
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const DetailItem = styled.div`
  padding: 12px;
  background: ${neutral[1]};
  border-radius: 8px;
  
  .label {
    font-size: 11px;
    color: ${theme.textSubtle};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 4px;
  }
  
  .value {
    font-size: 14px;
    color: ${theme.textPrimary};
    word-break: break-all;
    
    &.mono {
      font-family: 'SF Mono', monospace;
      font-size: 12px;
    }
  }
`;

// Action types for icons
const ACTION_ICONS: Record<string, string> = {
  CREATE: '➕',
  UPDATE: '✏️',
  DELETE: '🗑️',
  READ: '👁️',
  ACCESS: '🔓',
  LOGIN: '🔐',
  LOGOUT: '🚪',
  APPROVE: '✅',
  REJECT: '❌',
  REVOKE: '🚫',
  DEFAULT: '📋',
};

const getActionIcon = (action: string): string => {
  const upperAction = action?.toUpperCase() || '';
  for (const [key, icon] of Object.entries(ACTION_ICONS)) {
    if (upperAction.includes(key)) return icon;
  }
  return ACTION_ICONS.DEFAULT;
};

export const AuditLogs: React.FC = () => {
  const [logs, setLogs] = useState<AuditLog[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
  
  // Verification state
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<boolean | null>(null);

  // Filters
  const [query, setQuery] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [entityFilter, setEntityFilter] = useState('');
  const [resultFilter, setResultFilter] = useState('');

  const loadLogs = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await adminApi.getAuditLogs({
        page: pagination.page,
        pageSize: pagination.pageSize,
        query: query || undefined,
        action: actionFilter || undefined,
        entityType: entityFilter || undefined,
        result: resultFilter || undefined,
      });
      
      setLogs(response.data || []);
      setPagination(prev => ({
        ...prev,
        totalItems: response.pagination?.totalItems || 0,
        totalPages: response.pagination?.totalPages || 0,
      }));
    } catch (err: any) {
      setError(err.message || 'Failed to load audit logs');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, query, actionFilter, entityFilter, resultFilter]);

  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    loadLogs();
  };

  const handleClearFilters = () => {
    setQuery('');
    setActionFilter('');
    setEntityFilter('');
    setResultFilter('');
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleExportCsv = async () => {
    try {
      const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';
      const token = localStorage.getItem('authToken') || localStorage.getItem('token');
      const userInfo = localStorage.getItem('userInfo');
      const params = new URLSearchParams();
      if (userInfo) {
        try {
          const user = JSON.parse(userInfo);
          if (user.tenantId) params.set('tenantId', user.tenantId);
        } catch (e) { /* ignore */ }
      }
      const response = await fetch(`${API_BASE}/admin/v1/audit-logs/export/csv?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'audit-logs.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Export failed', err);
    }
  };

  const handleVerifyIntegrity = async (log: AuditLog) => {
    try {
      setVerifying(true);
      setVerifyResult(null);
      const response = await adminApi.verifyAuditLogHash(log.timestamp);
      setVerifyResult(response.verified);
    } catch (err) {
      console.error('Verification failed', err);
      setVerifyResult(false);
    } finally {
      setVerifying(false);
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return {
      date: formatDate(timestamp),
      time: formatTime(timestamp),
    };
  };

  return (
    <AdminShell title="Audit Logs">
      <SharedPageHeader>
        <TitleSection>
          <h1>📜 Audit Logs</h1>
          <p>Complete audit trail of all system activities for compliance</p>
        </TitleSection>
        <ExportButton onClick={handleExportCsv}>📥 Export CSV</ExportButton>
      </SharedPageHeader>

      <FilterBar>
        <FilterGroupWithLabel>
          <label>Search</label>
          <SharedSearchInput
            type="text"
            placeholder="Search by entity ID, actor..."
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setQuery(e.target.value)}
            onKeyPress={(e: React.KeyboardEvent) => e.key === 'Enter' && handleSearch()}
          />
        </FilterGroupWithLabel>
        
        <FilterGroupWithLabel>
          <label>Action</label>
          <SelectInput value={actionFilter} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setActionFilter(e.target.value)}>
            <option value="">All Actions</option>
            <option value="CONSENT_GIVEN">Consent Given</option>
            <option value="CONSENT_REVOKED">Consent Revoked</option>
            <option value="TEMPLATE_CREATED">Template Created</option>
            <option value="TEMPLATE_UPDATED">Template Updated</option>
            <option value="DATA_ACCESS">Data Access</option>
            <option value="LOGIN">Login</option>
            <option value="LOGOUT">Logout</option>
          </SelectInput>
        </FilterGroupWithLabel>
        
        <FilterGroupWithLabel>
          <label>Entity Type</label>
          <SelectInput value={entityFilter} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setEntityFilter(e.target.value)}>
            <option value="">All Entities</option>
            <option value="CONSENT">Consent</option>
            <option value="TEMPLATE">Template</option>
            <option value="PROJECT">Project</option>
            <option value="DATA_OWNER">Data Owner</option>
            <option value="USER">User</option>
            <option value="TENANT">Tenant</option>
          </SelectInput>
        </FilterGroupWithLabel>
        
        <FilterGroupWithLabel>
          <label>Result</label>
          <SelectInput value={resultFilter} onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setResultFilter(e.target.value)}>
            <option value="">All Results</option>
            <option value="SUCCESS">Success</option>
            <option value="FAILURE">Failure</option>
          </SelectInput>
        </FilterGroupWithLabel>
        
        <ClearButton onClick={handleClearFilters}>Clear Filters</ClearButton>
      </FilterBar>

      {error && <ErrorState $variant="error">⚠️ {error}</ErrorState>}

      {loading ? (
        <LoadingSpinner>Loading audit logs...</LoadingSpinner>
      ) : logs.length === 0 ? (
        <EmptyStateContainer>
          <EmptyStateIcon>📜</EmptyStateIcon>
          <EmptyStateTitle>No Audit Logs Found</EmptyStateTitle>
          <EmptyStateText>Try adjusting your filters or check back later</EmptyStateText>
        </EmptyStateContainer>
      ) : (
        <>
          <Table>
            <thead>
              <tr>
                <Th>Timestamp</Th>
                <Th>Action</Th>
                <Th>Entity</Th>
                <Th>Project</Th>
                <Th>Consent Status</Th>
                <Th>Actor</Th>
                <Th>Result</Th>
                <Th>Compliance</Th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => {
                const { date, time } = formatTimestamp(log.timestamp);
                return (
                  <Tr key={log.auditId} onClick={() => { setSelectedLog(log); setVerifyResult(null); }}>
                    <Td>
                      <TimeCell>
                        <div className="date">{date}</div>
                        <div className="time">{time}</div>
                      </TimeCell>
                    </Td>
                    <Td>
                      <SharedActionBadge $action={log.action}>
                        {getActionIcon(log.action)} {log.action?.replace(/_/g, ' ')}
                      </SharedActionBadge>
                    </Td>
                    <Td>
                      <EntityTag>{log.entityType}</EntityTag>
                      <div style={{ fontSize: '11px', color: theme.textSubtle, marginTop: '4px', fontFamily: 'monospace' }}>
                        {log.entityId?.substring(0, 20)}...
                      </div>
                    </Td>
                    <Td>
                      {log.projectId ? (
                        <div style={{ fontSize: '12px', fontFamily: 'monospace', color: theme.textPrimary }}>
                          {log.projectId.substring(0, 16)}...
                        </div>
                      ) : (
                        <span style={{ color: theme.textSubtle, fontSize: '12px' }}>--</span>
                      )}
                    </Td>
                    <Td>
                      {log.consentStatus ? (
                        <ConsentStatusBadge $status={log.consentStatus}>{log.consentStatus}</ConsentStatusBadge>
                      ) : (
                        <span style={{ color: theme.textSubtle, fontSize: '12px' }}>--</span>
                      )}
                    </Td>
                    <Td>
                      <ActorCell>
                        <div className="email">{log.actorEmail || log.actorId || 'System'}</div>
                        {log.actorRole && <div className="role">{log.actorRole}</div>}
                        {log.ipAddress && <div className="ip">{log.ipAddress}</div>}
                      </ActorCell>
                    </Td>
                    <Td>
                      <ResultBadge $status={log.result}>{log.result}</ResultBadge>
                    </Td>
                    <Td>
                      <ComplianceBadges>
                        {log.gdprRelevant && <ComplianceBadge $type="gdpr">GDPR</ComplianceBadge>}
                        {log.dpdpRelevant && <ComplianceBadge $type="dpdp">DPDP</ComplianceBadge>}
                      </ComplianceBadges>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>

          <PaginationContainer>
            <PaginationInfo>
              Showing {((pagination.page - 1) * pagination.pageSize) + 1} - {Math.min(pagination.page * pagination.pageSize, pagination.totalItems)} of {pagination.totalItems} logs
            </PaginationInfo>
            <PaginationButtons>
              <PaginationButton
                disabled={pagination.page <= 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                ← Previous
              </PaginationButton>
              <PaginationButton $active>{pagination.page}</PaginationButton>
              <PaginationButton
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Next →
              </PaginationButton>
            </PaginationButtons>
          </PaginationContainer>
        </>
      )}

      {/* Detail Modal */}
      <ModalOverlay $show={!!selectedLog} onClick={() => { setSelectedLog(null); setVerifyResult(null); }}>
        <ModalContent onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          <ModalHeader>
            <ModalTitle>📋 Audit Log Details</ModalTitle>
            <CloseButton onClick={() => { setSelectedLog(null); setVerifyResult(null); }}>×</CloseButton>
          </ModalHeader>
          {selectedLog && (
            <ModalBody>
              <DetailSection>
                <SectionTitle>📌 Event Information</SectionTitle>
                <DetailGrid>
                  <DetailItem>
                    <div className="label">Audit ID</div>
                    <div className="value mono">{selectedLog.auditId}</div>
                  </DetailItem>
                  <DetailItem>
                    <div className="label">Timestamp</div>
                    <div className="value">{formatDate(selectedLog.timestamp)} {formatTime(selectedLog.timestamp)}</div>
                  </DetailItem>
                  <DetailItem>
                    <div className="label">Action</div>
                    <div className="value">
                      <SharedActionBadge $action={selectedLog.action}>
                        {getActionIcon(selectedLog.action)} {selectedLog.action}
                      </SharedActionBadge>
                    </div>
                  </DetailItem>
                  <DetailItem>
                    <div className="label">Result</div>
                    <div className="value">
                      <ResultBadge $status={selectedLog.result}>{selectedLog.result}</ResultBadge>
                    </div>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <SectionTitle>🎯 Entity</SectionTitle>
                <DetailGrid>
                  <DetailItem>
                    <div className="label">Entity Type</div>
                    <div className="value">{selectedLog.entityType}</div>
                  </DetailItem>
                  <DetailItem>
                    <div className="label">Entity ID</div>
                    <div className="value mono">{selectedLog.entityId}</div>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <SectionTitle>👤 Actor</SectionTitle>
                <DetailGrid>
                  <DetailItem>
                    <div className="label">Actor ID</div>
                    <div className="value mono">{selectedLog.actorId || 'N/A'}</div>
                  </DetailItem>
                  <DetailItem>
                    <div className="label">Actor Type</div>
                    <div className="value">{selectedLog.actorType || 'N/A'}</div>
                  </DetailItem>
                  <DetailItem>
                    <div className="label">Email</div>
                    <div className="value">{selectedLog.actorEmail || 'N/A'}</div>
                  </DetailItem>
                  <DetailItem>
                    <div className="label">Role</div>
                    <div className="value">{selectedLog.actorRole || 'N/A'}</div>
                  </DetailItem>
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <SectionTitle>🌐 Request Context</SectionTitle>
                <DetailGrid>
                  <DetailItem>
                    <div className="label">IP Address</div>
                    <div className="value mono">{selectedLog.ipAddress || 'N/A'}</div>
                  </DetailItem>
                  <DetailItem>
                    <div className="label">Request ID</div>
                    <div className="value mono">{selectedLog.requestId || 'N/A'}</div>
                  </DetailItem>
                  {selectedLog.userAgent && (
                    <DetailItem style={{ gridColumn: '1 / -1' }}>
                      <div className="label">User Agent</div>
                      <div className="value" style={{ fontSize: '12px' }}>{selectedLog.userAgent}</div>
                    </DetailItem>
                  )}
                </DetailGrid>
              </DetailSection>

              {selectedLog.changeDescription && (
                <DetailSection>
                  <SectionTitle>📝 Change Description</SectionTitle>
                  <DetailItem>
                    <div className="value">{selectedLog.changeDescription}</div>
                  </DetailItem>
                </DetailSection>
              )}

              {selectedLog.errorMessage && (
                <DetailSection>
                  <SectionTitle>⚠️ Error Details</SectionTitle>
                  <DetailItem style={{ background: garnetRed[1] }}>
                    <div className="value" style={{ color: garnetRed[6] }}>{selectedLog.errorMessage}</div>
                  </DetailItem>
                </DetailSection>
              )}

              {selectedLog.projectId && (
                <DetailSection>
                  <SectionTitle>📁 Project</SectionTitle>
                  <DetailGrid>
                    <DetailItem>
                      <div className="label">Project ID</div>
                      <div className="value mono">{selectedLog.projectId}</div>
                    </DetailItem>
                    {selectedLog.consentStatus && (
                      <DetailItem>
                        <div className="label">Consent Status</div>
                        <div className="value">
                          <ConsentStatusBadge $status={selectedLog.consentStatus}>{selectedLog.consentStatus}</ConsentStatusBadge>
                        </div>
                      </DetailItem>
                    )}
                  </DetailGrid>
                </DetailSection>
              )}

              <DetailSection>
                <SectionTitle>📋 Compliance</SectionTitle>
                <DetailGrid>
                  <DetailItem>
                    <div className="label">GDPR Relevant</div>
                    <div className="value">{selectedLog.gdprRelevant ? '✅ Yes' : '❌ No'}</div>
                  </DetailItem>
                  <DetailItem>
                    <div className="label">DPDP Relevant</div>
                    <div className="value">{selectedLog.dpdpRelevant ? '✅ Yes' : '❌ No'}</div>
                  </DetailItem>
                  {selectedLog.complianceNotes && (
                    <DetailItem style={{ gridColumn: '1 / -1' }}>
                      <div className="label">Compliance Notes</div>
                      <div className="value">{selectedLog.complianceNotes}</div>
                    </DetailItem>
                  )}
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <SectionTitle>🔒 Integrity Verification</SectionTitle>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <VerifyButton
                    onClick={() => handleVerifyIntegrity(selectedLog)}
                    disabled={verifying}
                  >
                    {verifying ? '⏳ Verifying...' : '🔍 Verify Integrity'}
                  </VerifyButton>
                  {verifyResult !== null && (
                    <VerifyResult $verified={verifyResult}>
                      {verifyResult ? '✅ Verified — Hash integrity confirmed' : '⚠️ Tampered — Hash mismatch detected'}
                    </VerifyResult>
                  )}
                </div>
              </DetailSection>
            </ModalBody>
          )}
        </ModalContent>
      </ModalOverlay>
    </AdminShell>
  );
};

export default AuditLogs;
